import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const phases = [
  {
    phase: 'Discovery',
    duration: 'Week 1-2',
    activities: [
      'System mapping and analysis',
      'Stakeholder interviews',
      'Data collection and review',
      'Constraint hypothesis formation',
    ],
  },
  {
    phase: 'Analysis',
    duration: 'Week 2-3',
    activities: [
      'Framework application across all ten multipliers',
      'Leverage point identification',
      'Impact modeling and prioritization',
      'Intervention design',
    ],
  },
  {
    phase: 'Execution',
    duration: 'Week 4-12',
    activities: [
      'Targeted system interventions',
      'Framework implementation',
      'Real-time adjustment and optimization',
      'Impact measurement',
    ],
  },
  {
    phase: 'Measurement',
    duration: 'Ongoing',
    activities: [
      'Output tracking and analysis',
      'Before/after comparison',
      'ROI documentation',
      'Next constraint identification',
    ],
  },
];

interface PhaseCardProps {
  phase: typeof phases[number];
  index: number;
}

const DesktopPhaseCard = ({ phase, index }: PhaseCardProps) => {
  const phaseRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: phaseProgress } = useScroll({
    target: phaseRef,
    offset: ['start end', 'center center'],
  });

  const phaseOpacity = useTransform(phaseProgress, [0, 0.4, 0.7], [0, 0.6, 1]);
  const phaseX = useTransform(
    phaseProgress,
    [0, 0.7],
    [index % 2 === 0 ? -60 : 60, 0]
  );
  const phaseScale = useTransform(phaseProgress, [0, 0.7], [0.9, 1]);

  return (
    <motion.div
      ref={phaseRef}
      style={{ opacity: phaseOpacity, x: phaseX, scale: phaseScale }}
      className={`flex items-center gap-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
    >
      <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
        <div className="bg-dark-card border border-dark-border rounded-2xl p-8 hover:border-accent-blue/50 transition-all duration-300">
          <h3 className="text-3xl font-bold text-white mb-2">{phase.phase}</h3>
          <p className="text-accent-blue font-medium mb-6">{phase.duration}</p>
          <ul className={`space-y-3 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
            {phase.activities.map((activity, i) => (
              <li key={i} className="text-[15px] text-gray-400 leading-relaxed">
                {activity}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="relative z-10">
        <div className="w-16 h-16 bg-accent-blue rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-accent-blue/20">
          {index + 1}
        </div>
      </div>

      <div className="flex-1" />
    </motion.div>
  );
};

const MobilePhaseCard = ({ phase, index }: PhaseCardProps) => {
  const mobilePhaseRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: mobileProgress } = useScroll({
    target: mobilePhaseRef,
    offset: ['start end', 'center center'],
  });

  const mobileOpacity = useTransform(mobileProgress, [0, 0.5, 0.8], [0, 0.6, 1]);
  const mobileY = useTransform(mobileProgress, [0, 0.8], [50, 0]);

  return (
    <motion.div
      ref={mobilePhaseRef}
      style={{ opacity: mobileOpacity, y: mobileY }}
      className="relative"
    >
      {index < phases.length - 1 && (
        <div className="absolute left-8 top-20 bottom-0 w-0.5 bg-dark-border transform translate-y-4" />
      )}

      <div className="flex gap-4 md:gap-6">
        <div className="relative z-10 flex-shrink-0">
          <div className="w-16 h-16 bg-accent-blue rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-accent-blue/20">
            {index + 1}
          </div>
        </div>

        <div className="flex-1 bg-dark-card border border-dark-border rounded-xl md:rounded-2xl p-6 md:p-8">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{phase.phase}</h3>
          <p className="text-accent-blue font-medium text-sm md:text-base mb-5 md:mb-6">
            {phase.duration}
          </p>
          <ul className="space-y-2.5 md:space-y-3">
            {phase.activities.map((activity, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="text-accent-blue mt-1.5 flex-shrink-0">•</span>
                <span className="text-sm md:text-[15px] text-gray-400 leading-relaxed">
                  {activity}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

const ProcessTimeline = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const glowIntensity = useTransform(scrollYProgress, [0.2, 0.5, 0.8], [0.05, 0.12, 0.05]);

  return (
    <section ref={sectionRef} className="relative bg-dark-light py-20 md:py-32 lg:py-40 overflow-hidden">
      {/* Continuous Background Grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(20, 184, 166, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(20, 184, 166, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      {/* Dynamic Background Glow */}
      <motion.div
        className="absolute top-1/3 right-10 w-[500px] h-[500px] bg-accent-blue rounded-full blur-3xl pointer-events-none"
        style={{ opacity: glowIntensity }}
      />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-12 md:mb-20 lg:mb-24"
        >
          The Process
        </motion.h2>

        {/* Desktop Timeline */}
        <div className="hidden lg:block relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-dark-border transform -translate-x-1/2" />
          <div className="space-y-24">
            {phases.map((phase, index) => (
              <DesktopPhaseCard key={phase.phase} phase={phase} index={index} />
            ))}
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="lg:hidden space-y-8 md:space-y-10">
          {phases.map((phase, index) => (
            <MobilePhaseCard key={phase.phase} phase={phase} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
